import type { ClientOptions, TDefaultBody } from './types';
import { base } from './base';

interface ClientGlobalOptions {
  /**
   * Default headers applied to every request
   */
  headers: HeadersInit;
  /**
   * Base URL to resolve relative URLs against
   *
   * Defaults to the current origin (window.location.origin)
   */
  baseURL?: string;
  /**
   * Error handler called when the client encounters an error
   * @param error - Error thrown by the client
   */
  onError: (error: unknown) => never;
  /**
   * Fetch implementation to use (defaults to global fetch)
   */
  fetch: typeof fetch;
}

const globalOptions: ClientGlobalOptions = {
  headers: {},
  onError: (error: unknown) => {
    throw error;
  },
  fetch,
};

export const client = async <TResponse = unknown, TBody extends TDefaultBody = TDefaultBody>(
  input: string,
  options?: ClientOptions<TResponse, TBody>
): Promise<TResponse> => {
  const opts = options ?? {};

  opts.headers = {
    ...globalOptions.headers,
    ...opts.headers,
  };

  try {
    return await base<TResponse, TBody>(
      new URL(input, globalOptions.baseURL ?? window.location.origin),
      {
        ...opts,
        fetch: globalOptions.fetch,
      }
    );
  } catch (error) {
    globalOptions.onError(error);
  }
};

client.init = (options: Partial<ClientGlobalOptions>): void => {
  Object.defineProperties(globalOptions, Object.getOwnPropertyDescriptors(options));
};
